import { useState, useCallback, useEffect } from "react";
import { useInsideDialog } from "../Dialog/Internal/InsideDialogContext";

// ---------------------------------------------------------------------------
// Codec interface
// ---------------------------------------------------------------------------

export interface FilterCodec<T> {
  serialize: (value: T) => string | undefined;
  deserialize: (raw: string) => T;
}

// ---------------------------------------------------------------------------
// Built-in codecs
// ---------------------------------------------------------------------------

export const FilterCodecs = {
  /**
   * Serialize `[LocalDate, LocalDate] | undefined` as comma-separated.
   * URL example: `?dates=2026-01-01,2026-03-31`
   */
  dateRange(): FilterCodec<[string, string] | undefined> {
    return {
      serialize(value) {
        if (!value) return undefined;
        return `${value[0]},${value[1]}`;
      },
      deserialize(raw) {
        const parts = raw.split(",");
        if (parts.length === 2 && parts[0] && parts[1]) {
          return [parts[0], parts[1]];
        }
        return undefined;
      },
    };
  },

  /**
   * Serialize `string[]` whose values are free text that can contain commas
   * (company names, business names) — the default array codec would split
   * those into two bogus entries. Values that can never contain a comma —
   * ids, enum members, emails — should use the default array codec.
   *
   * This codec escapes the comma to `%2C` and joins on `,`; the URL itself is
   * then written through `URLSearchParams`, which escapes that `%` again. So
   * `["Hartzell Propeller, Inc", "Bolt Supply"]` reaches the address bar as
   * `?company=Hartzell+Propeller%252C+Inc%2CBolt+Supply` — the doubly-escaped
   * `%252C` is the comma inside a value, the plain `%2C` is the separator.
   * Hand-writing the single-escaped form yields three entries, not two.
   */
  stringList(): FilterCodec<string[]> {
    return {
      serialize(value) {
        if (value.length === 0) return undefined;
        return value
          .map((item) => item.replaceAll("%", "%25").replaceAll(",", "%2C"))
          .join(",");
      },
      deserialize(raw) {
        if (!raw) return [];
        // Unescape the comma first: a literal "%2C" in the value serialized to
        // "%252C", which holds no "%2C" of its own, so this pass leaves it be
        // and the "%25" pass restores it.
        return raw
          .split(",")
          .map((item) => item.replaceAll("%2C", ",").replaceAll("%25", "%"));
      },
    };
  },

  /**
   * Serialize a value drawn from a fixed set of options — a dropdown's values.
   * Nothing validates a URL, so a hand-edited or stale param would otherwise
   * reach the page as-is: `?doneWindow=abc` becomes `NaN` in a fetch, and an
   * unrecognised `?groupBy=` silently falls through to whichever branch is
   * last. Anything outside `allowed` deserializes to `fallback` instead.
   * URL example: `?groupBy=carrier`
   */
  oneOf<T extends string>(allowed: readonly T[], fallback: T): FilterCodec<T> {
    return {
      serialize: (value) => (value === fallback ? undefined : value),
      deserialize: (raw) =>
        allowed.includes(raw as T) ? (raw as T) : fallback,
    };
  },

  /**
   * Serialize a complex object as JSON. Strips empty arrays and fields
   * matching defaults before writing. Removes param entirely when the
   * whole object matches default.
   */
  // oxlint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- T is inferred from defaultValue and used in FilterCodec<T>
  json<T extends object>(defaultValue?: T): FilterCodec<T> {
    return {
      serialize(value) {
        if (defaultValue && deepEqual(value, defaultValue)) return undefined;

        // Strip fields that match the default or are empty arrays
        const cleaned: Record<string, unknown> = {};
        for (const [key, v] of Object.entries(value)) {
          if (Array.isArray(v) && v.length === 0) continue;
          if (
            defaultValue &&
            key in defaultValue &&
            deepEqual(v, (defaultValue as never)[key])
          )
            continue;
          cleaned[key] = v;
        }

        if (Object.keys(cleaned).length === 0) return undefined;
        return JSON.stringify(cleaned);
      },
      deserialize(raw) {
        try {
          const parsed = JSON.parse(raw);
          if (defaultValue) return { ...defaultValue, ...parsed };
          return parsed as T;
        } catch {
          return defaultValue as T;
        }
      },
    };
  },
};

// ---------------------------------------------------------------------------
// Auto-detected codecs based on default value type
// ---------------------------------------------------------------------------

function autoCodec<T>(defaultValue: T): FilterCodec<T> {
  // string
  if (typeof defaultValue === "string") {
    return {
      serialize: (v) => (v === defaultValue ? undefined : String(v)),
      deserialize: (raw) => raw as T,
    } as FilterCodec<T>;
  }

  // undefined (optional string)
  if (defaultValue === undefined) {
    return {
      serialize: (v) => (v === undefined ? undefined : String(v)),
      deserialize: (raw) => raw as T,
    } as FilterCodec<T>;
  }

  // boolean
  if (typeof defaultValue === "boolean") {
    return {
      serialize: (v) => (v === defaultValue ? undefined : "1"),
      deserialize: (raw) => (raw === "1") as T,
    } as FilterCodec<T>;
  }

  // number
  if (typeof defaultValue === "number") {
    return {
      serialize: (v) => (v === defaultValue ? undefined : String(v)),
      deserialize: (raw) => {
        const n = Number(raw);
        return (Number.isFinite(n) ? n : defaultValue) as T;
      },
    } as FilterCodec<T>;
  }

  // string array
  if (Array.isArray(defaultValue)) {
    return {
      serialize: (v) => {
        const arr = Array.isArray(v) ? v : [];
        return arr.length === 0 ? undefined : arr.join(",");
      },
      deserialize: (raw) => {
        return (raw ? raw.split(",") : []) as T;
      },
    } as FilterCodec<T>;
  }

  // Fallback: JSON — at this point T must be an object (string/number/boolean/array eliminated above)
  const objDefault: Record<string, unknown> = Object.assign({}, defaultValue);
  return FilterCodecs.json(objDefault) as FilterCodec<T>; // safe-cast
}

// ---------------------------------------------------------------------------
// URL helpers (mirrors Tabs pattern)
// ---------------------------------------------------------------------------

function readParam(key: string): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) ?? undefined;
}

function writeParams(updates: Record<string, string | undefined>) {
  const url = new URL(window.location.href);
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  }
  window.history.replaceState({}, "", url.toString());
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Drop-in replacement for `useState` that syncs state to URL query params.
 *
 * - Reads initial value from URL on mount (falls back to `defaultValue`)
 * - Writes changes via `history.replaceState` (no history entries)
 * - Skips URL sync inside dialogs (via `useInsideDialog()`)
 * - Values matching `defaultValue` are removed from the URL
 *
 * ```ts
 * const [carrier, setCarrier] = useFilterParam("carrier", "");
 * const [flagged, setFlagged] = useFilterParam("flagged", false);
 * const [range, setRange] = useFilterParam("dates", undefined, FilterCodecs.dateRange());
 * ```
 */
export function useFilterParam<T>(
  key: string,
  defaultValue: T,
  codec?: FilterCodec<T>,
): [T, (value: T | ((prev: T) => T)) => void] {
  const insideDialog = useInsideDialog();
  const syncToUrl = !insideDialog;
  const resolvedCodec = codec ?? autoCodec(defaultValue);

  const [value, setValueRaw] = useState<T>(() => {
    if (!syncToUrl) return defaultValue;
    const raw = readParam(key);
    if (raw === undefined) return defaultValue;
    return resolvedCodec.deserialize(raw);
  });

  // Re-read from URL on popstate (e.g. Quick Lookup navigating to this page)
  useEffect(() => {
    if (!syncToUrl) return;
    function onPopState() {
      const raw = readParam(key);
      const next =
        raw === undefined ? defaultValue : resolvedCodec.deserialize(raw);
      setValueRaw(next);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [syncToUrl, key, defaultValue, resolvedCodec]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueRaw((prev) => {
        const resolved =
          typeof next === "function" ? (next as (prev: T) => T)(prev) : next;

        if (syncToUrl) {
          const serialized = resolvedCodec.serialize(resolved);
          writeParams({ [key]: serialized });
        }

        return resolved;
      });
    },
    [syncToUrl, key, resolvedCodec],
  );

  return [value, setValue];
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // oxlint-disable-next-line fs/no-null -- deepEqual must handle null from JSON.parse
  if (a === undefined || a === null || b === undefined || b === null)
    return false;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }

  if (typeof a === "object" && typeof b === "object") {
    const objA = a as never;
    const objB = b as never;
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => deepEqual(objA[k], objB[k]));
  }

  return false;
}
